import React, { useState, useEffect } from 'react';
import mixpanel from 'mixpanel-browser';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, FormControlLabel } from '@mui/material';

import NavBar from '../../NavBar';

import { AreaRatesSearch } from '../AreaRatesSearch';

import { MortgageFinancing } from './MortgageFinancing';

import { LoanTermTabs } from '../LoanTermTabs';
import { CreditTierTabs } from '../CreditTierTabs';
import { DownPaymentInput } from '../DownPaymentInput';
import { TradeInValueInput } from '../TradeInValueInput';
import { EstimateValueInput } from '../EstimateValueInput';
import { LoanAmount } from '../LoanAmount';
import { CheckRatesButton } from '../CheckRatesButton';
import { ResetRatesButton } from '../ResetRatesButton.js';
import { MinimizeOrFullScreenButton } from '../MinimizeOrFullScreenButton.js';

import { ComparisonSummary } from '../../RatesSummary/ComparisonSummary';

import { Footer } from '../../Footer/Footer';

import { useLocalStorage } from '../../../CustomHooks/useLocalStorage.js';

import { checkUserFilterRates, setUserInfo } from '../../../actions/index.js';

import { getOrSetAnonymousUserId } from '../../../utils/index.js';

import { utm_email_type, LENDER_ZIP_MAP } from '../__fixtures__/utm_campaign_types.js';

import {
  NY,
  EXCELLENT,
  CREDIT_SCORE_OBJ,
  state_rates_filter,
  zip_code_rates_filter,
} from '../../../constants/LoansCalculator';

import { TOKEN, LOGOUT, handleSettingsClick } from '../../../constants/index.js';

import {
  MORTGAGE_LOAN_RATES_HEADER,
  MORTGAGE_LOAN_DETAILS_HEADER,
  HAS_TRADE_IN,
  MORTGAGE_LOAN_TERMS,
  DEFAULT_SELECTED_MORTGAGE_TERMS,
} from '../../../constants/LoansCalculator/Mortgages';

import './index.css';

export const MortgageLoans = () => {
  const [zipCode, setZipCode] = useState('12025');
  const [usState, setUsState] = useState(NY);

  const [houseOfWorshipZipCode, setHouseOfWorshipZipCode] = useState('');
  const [houseOfWorshipUsState, setHouseOfWorshipUsState] = useState(NY);
  const [employmentZipCode, setEmploymentZipCode] = useState('');
  const [employmentUsState, setEmploymentUsState] = useState(NY);

  const [selectedMortgageTerms, setSelectedMortgageTerm] = useState(
    DEFAULT_SELECTED_MORTGAGE_TERMS,
  );
  const [selectedCreditTier, setSelectedCreditTier] = useState(EXCELLENT);
  const [downPaymentValue, setDownPaymentValue] = useState(0);
  const [hasTradeIn, setHasTradeIn] = useState(false);
  const [tradeInValue, setTradeInValue] = useState(0);
  const [estimateValue, setEstimateValue] = useState(0);
  const [loanAmountValue, setLoanAmountValue] = useState(0);

  const [checkRates, setCheckRates] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(true);

  const [segsList, setSegsList] = useState({ 'Select Savers Club': 'Select Savers Club' });
  const [isQuorumEligible, setIsQuorumEligible] = useState(true);

  const [value] = useLocalStorage(TOKEN);

  const dispatch = useDispatch();
  const { user, jwt, occupancyType, propertyType } = useSelector(
    ({
      userReducer: { user, jwt },
      residencyReducer,
      propertyReducer: { occupancyType, propertyType },
    }) => ({
      user,
      jwt,
      residencyReducer,
      occupancyType,
      propertyType,
    }),
  );

  useEffect(() => {
    if (value || jwt) {
      if (!localStorage.getItem(TOKEN)) {
        return;
      }
      const user = atob(localStorage.getItem(TOKEN)?.split('.')?.[1]);
      const parsedUser = JSON.parse(user);

      if (parsedUser) {
        const date = new Date(parsedUser.exp * 1000);
        const now = new Date();
        if (date < now) {
          handleSettingsClick[LOGOUT]?.(dispatch, parsedUser);
        }

        dispatch((dispatch) => setUserInfo(dispatch, parsedUser));
      }
    } else {
      const anonymousUserId = getOrSetAnonymousUserId();

      dispatch((dispatch) => setUserInfo(dispatch, { anonymousUserId }));
    }
  }, [value, jwt]);

  const utm_campaign = new URLSearchParams(window.location.search).get('utm_campaign');

  const utm_campaign_type_name = new URLSearchParams(window.location.search).get('utm_email_type');

  const utm_lender_name = new URLSearchParams(window.location.search).get('utm_lender_name');

  useEffect(() => {
    if (utm_campaign && utm_campaign_type_name) {
      const zipCodes = LENDER_ZIP_MAP[utm_lender_name];

      if (zipCodes && zipCodes.length && utm_email_type[utm_campaign_type_name]) {
        const currentNum = Math.floor(Math.random() * zipCodes.length);
        const currZipCode = zipCodes.find((_, index) => index === currentNum);

        handleUTMCampaignCheckRates({
          utm_email_type_name: utm_campaign_type_name,
          zipCode: currZipCode,
        });
      }
    }
  }, []);

  const handleZipCodeChange = (val) => {
    setZipCode((prevState) => {
      mixpanel.track('Mortgage_Calculator', {
        fieldInput: 'Zip Code',
        field_value: val,
      });
      if (val.length <= 5) {
        return val;
      }
      return prevState;
    });
  };

  const handleUsStateChange = (val) => {
    mixpanel.track('Mortgage_Calculator', {
      fieldInput: 'US State',
      field_value: val,
    });
    setUsState(val);
  };

  const handleHouseOfWorshipZipCodeChange = (val) => {
    mixpanel.track('Mortgage_Calculator', {
      fieldInput: 'House Of Worship Zip Code',
      field_value: val,
    });
    setHouseOfWorshipZipCode((prevState) => {
      if (val.length <= 5) {
        return val;
      }

      return prevState;
    });
  };

  const handleHouseOfWorshipStateChange = (val) => {
    mixpanel.track('Mortgage_Calculator', {
      fieldInput: 'House of Worship US State',
      field_value: val,
    });
    setHouseOfWorshipUsState(val);
  };

  const handleEmploymentZipCodeChange = (val) => {
    mixpanel.track('Mortgage_Calculator', {
      fieldInput: 'Employment Zip Code',
      field_value: val,
    });

    setEmploymentZipCode((prevState) => {
      if (val.length <= 5) {
        return val;
      }

      return prevState;
    });
  };

  const handleEmploymentStateChange = (val) => {
    mixpanel.track('Mortgage_Calculator', {
      fieldInput: 'Employment US State',
      field_value: val,
    });

    setEmploymentUsState(val);
  };

  const handleLoanTerm = (loanTerm) => {
    setSelectedMortgageTerm((prevState) => {
      if (prevState[loanTerm]) {
        const copyState = { ...prevState };
        delete copyState[loanTerm];

        mixpanel.track('Mortgage_Calculator', {
          fieldInput: 'Loan Terms',
          LOAN_TERMS: copyState,
        });

        return { ...copyState };
      }

      const updatedLoanTerms = { ...prevState, [loanTerm]: Number(loanTerm) * 12 };

      mixpanel.track('Mortgage_Calculator', {
        fieldInput: 'Loan Terms',
        LOAN_TERMS: updatedLoanTerms,
      });
      return updatedLoanTerms;
    });
  };

  const handleSelectCreditTier = (creditTier) => {
    setSelectedCreditTier(creditTier);

    mixpanel.track('Mortgage_Calculator', {
      fieldInput: 'CREDIT TIER',
      CREDIT_TIER: creditTier,
    });
  };

  const handleDownPaymentChange = (value) => {
    setDownPaymentValue(value);

    const totalDepositValue = Number(value) + Number(tradeInValue);

    const NEW_LOAN_AMOUNT = Number(estimateValue) - Number(totalDepositValue);

    mixpanel.track('Mortgage_Calculator', {
      fieldInput: 'Down Payment',
      DownPaymentValue: value,
      LoanAmountValue: NEW_LOAN_AMOUNT,
    });

    setLoanAmountValue(NEW_LOAN_AMOUNT);
  };

  const handleTradeInValueChange = (value) => {
    setTradeInValue(value);
    const totalDepositValue = Number(downPaymentValue) + Number(value);

    const NEW_LOAN_AMOUNT = Number(estimateValue) - totalDepositValue;

    mixpanel.track('Mortgage_Calculator', {
      fieldInput: 'Trade In Value',
      TradeInValue: value,
      LoanAmountValue: NEW_LOAN_AMOUNT,
    });

    setLoanAmountValue(NEW_LOAN_AMOUNT);
  };

  const handleEstimateValueChange = (value) => {
    setEstimateValue(value);

    const totalDepositValue = Number(downPaymentValue) + Number(tradeInValue);

    const NEW_LOAN_AMOUNT = Number(value) - totalDepositValue;

    mixpanel.track('Mortgage_Calculator', {
      fieldInput: 'Estimate Value',
      EstimateValue: value,
      LoanAmountValue: NEW_LOAN_AMOUNT,
    });

    setLoanAmountValue(NEW_LOAN_AMOUNT);
  };

  const handleHasTradeIn = () => {
    setHasTradeIn((prevState) => {
      if (prevState) {
        handleTradeInValueChange(0);
      }

      mixpanel.track('Mortgage_Calculator', {
        fieldInput: 'Has Trade In? CheckBox',
        value: !prevState,
      });

      return !prevState;
    });
  };

  const handleUTMCampaignCheckRates = ({ utm_email_type_name, zipCode }) => {
    setCheckRates(true);
    setIsFullScreen(false);

    const anonymousUserId = getOrSetAnonymousUserId();

    mixpanel.track('Mortgage_Calculator', {
      fieldInput: 'Check Rates',
      [state_rates_filter]: usState,
      [zip_code_rates_filter]: zipCode,
      houseOfWorshipState: houseOfWorshipUsState,
      houseOfWorshipZipCode: houseOfWorshipZipCode,
      employmentUsState,
      employmentZipCode,
      loan_amount: loanAmountValue,
      loanAmount: loanAmountValue,
      credit_score: CREDIT_SCORE_OBJ[selectedCreditTier],
      creditTier: selectedCreditTier,
      loan_type: 'Mortgage',
      occupancyType,
      propertyType,
      segsList,
      isQuorumEligible,
      estimateValue,
      // term_months: Object.values(selectedMortgageTerms).map((mortgageInMonths) =>
      //   Number(mortgageInMonths),
      // ),
      mortgageLoanTermMonth: Object.values(selectedMortgageTerms).map((mortgageInMonths) =>
        Number(mortgageInMonths),
      ),
      down_payment: downPaymentValue,
      user: { anonymousUserId },
      ...utm_email_type[utm_email_type_name],
    });

    dispatch(
      checkUserFilterRates({
        body: {
          [state_rates_filter]: usState,
          [zip_code_rates_filter]: zipCode,
          houseOfWorshipState: houseOfWorshipUsState,
          houseOfWorshipZipCode: houseOfWorshipZipCode,
          employmentUsState,
          employmentZipCode,
          loan_amount: loanAmountValue,
          loanAmount: loanAmountValue,
          credit_score: CREDIT_SCORE_OBJ[selectedCreditTier],
          creditTier: selectedCreditTier,
          loan_type: 'Mortgage',
          occupancyType,
          propertyType,
          segsList,
          isQuorumEligible,
          estimateValue,
          // term_months: Object.values(selectedMortgageTerms).map((mortgageInMonths) =>
          //   Number(mortgageInMonths),
          // ),
          mortgageLoanTermMonth: Object.values(selectedMortgageTerms).map((mortgageInMonths) =>
            Number(mortgageInMonths),
          ),
          down_payment: downPaymentValue,
          user: { anonymousUserId },
          ...utm_email_type[utm_email_type_name],
        },
        financingInfo: {
          financingType: 'Home',
          selectedCreditTier,
          mortgageTerm: Object.keys(selectedMortgageTerms).reduce(
            (acc, curr) => ({
              ...acc,
              [selectedMortgageTerms[curr]]: Number(curr),
            }),
            {},
          ),
          mortgageTermInYears: Object.keys(selectedMortgageTerms).reduce(
            (acc, curr) => ({ ...acc, [curr]: Number(curr) }),
            {},
          ),
          downPayment: downPaymentValue,
          tradeInValue,
          estimateValue,
          ...utm_email_type[utm_email_type_name],
        },
        user: { anonymousUserId },
      }),
    );
  };

  const handleCheckRates = () => {
    setCheckRates(true);
    setIsFullScreen(false);

    mixpanel.track('Mortgage_Calculator', {
      fieldInput: 'Check Rates',
      [state_rates_filter]: usState,
      [zip_code_rates_filter]: zipCode,
      houseOfWorshipState: houseOfWorshipUsState,
      houseOfWorshipZipCode: houseOfWorshipZipCode,
      employmentUsState,
      employmentZipCode,
      loan_amount: loanAmountValue,
      loanAmount: loanAmountValue,
      credit_score: CREDIT_SCORE_OBJ[selectedCreditTier],
      creditTier: selectedCreditTier,
      loan_type: 'Mortgage',
      occupancyType,
      propertyType,
      segsList,
      isQuorumEligible,
      estimateValue,
      // term_months: Object.values(selectedMortgageTerms).map((mortgageInMonths) =>
      //   Number(mortgageInMonths),
      // ),
      mortgageLoanTermMonth: Object.values(selectedMortgageTerms).map((mortgageInMonths) =>
        Number(mortgageInMonths),
      ),
      down_payment: downPaymentValue,
      user,
    });

    dispatch(
      checkUserFilterRates({
        body: {
          [state_rates_filter]: usState,
          [zip_code_rates_filter]: zipCode,
          houseOfWorshipState: houseOfWorshipUsState,
          houseOfWorshipZipCode: houseOfWorshipZipCode,
          employmentUsState,
          employmentZipCode,
          loan_amount: loanAmountValue,
          loanAmount: loanAmountValue,
          credit_score: CREDIT_SCORE_OBJ[selectedCreditTier],
          creditTier: selectedCreditTier,
          loan_type: 'Mortgage',
          occupancyType,
          propertyType,
          segsList,
          isQuorumEligible,
          estimateValue,
          // term_months: Object.values(selectedMortgageTerms).map((mortgageInMonths) =>
          //   Number(mortgageInMonths),
          // ),
          mortgageLoanTermMonth: Object.values(selectedMortgageTerms).map((mortgageInMonths) =>
            Number(mortgageInMonths),
          ),
          down_payment: downPaymentValue,
          user,
        },
        financingInfo: {
          financingType: 'Home',
          selectedCreditTier,
          mortgageTerm: Object.keys(selectedMortgageTerms).reduce(
            (acc, curr) => ({
              ...acc,
              [selectedMortgageTerms[curr]]: Number(curr),
            }),
            {},
          ),
          mortgageTermInYears: Object.keys(selectedMortgageTerms).reduce(
            (acc, curr) => ({ ...acc, [curr]: Number(curr) }),
            {},
          ),
          downPayment: downPaymentValue,
          tradeInValue,
          estimateValue,
        },
        user,
      }),
    );
  };

  const handleResetRates = () => {
    setCheckRates(false);

    mixpanel.track('Mortgage_Calculator', {
      fieldInput: 'Reset Rates',
    });

    setUsState(NY);
    setZipCode('');
    setSelectedCreditTier(EXCELLENT);
    setDownPaymentValue(0);
    setHasTradeIn(false);
    setTradeInValue(0);
    setEstimateValue(0);
    setLoanAmountValue(0);
    setSelectedMortgageTerm(DEFAULT_SELECTED_MORTGAGE_TERMS);
  };

  return (
    <div>
      <NavBar currentPath={'Mortgages'} />
      <div className="auto-loans-filter-results-rates-wrapper">
        {(!checkRates || isFullScreen) && (
          <div className="loans-calculator-content-wrapper">
            <div className="auto-loans-calculator-wrapper">
              <h2>{MORTGAGE_LOAN_RATES_HEADER}</h2>

              <div className="auto-loans-calculator-content-wrapper">
                <div className="minimize-or-fullscreen-button-wrapper">
                  <MinimizeOrFullScreenButton
                    checkRates={checkRates}
                    isFullScreen={isFullScreen}
                    setIsFullScreen={setIsFullScreen}
                    filter={'Mortgage_Calculator'}
                  />
                </div>
                <AreaRatesSearch
                  zipCode={zipCode}
                  onZipCodeChange={handleZipCodeChange}
                  usState={usState}
                  onUsStateChange={handleUsStateChange}
                  houseOfWorshipZipCode={houseOfWorshipZipCode}
                  onHouseOfWorshipZipCodeChange={handleHouseOfWorshipZipCodeChange}
                  houseOfWorshipUsState={houseOfWorshipUsState}
                  onHouseOfWorshipUsStateChange={handleHouseOfWorshipStateChange}
                  employmentZipCode={employmentZipCode}
                  onEmploymenZipCodeChange={handleEmploymentZipCodeChange}
                  employmentUsState={employmentUsState}
                  onEmploymentStateChange={handleEmploymentStateChange}
                  filter="Home"
                  segsList={segsList}
                  setSegsList={setSegsList}
                  isQuorumEligible={isQuorumEligible}
                  setIsQuorumEligible={setIsQuorumEligible}
                />

                <h3 className="auto-loan-details-header">{MORTGAGE_LOAN_DETAILS_HEADER}</h3>

                <MortgageFinancing />

                <LoanTermTabs
                  selectedLoanTerms={selectedMortgageTerms}
                  financeLoanTerms={MORTGAGE_LOAN_TERMS}
                  handleLoanTermClick={handleLoanTerm}
                />
                <CreditTierTabs
                  selectedCreditTier={selectedCreditTier}
                  handleSelectCreditTier={handleSelectCreditTier}
                />

                <div className="mortgage-loans-financing-field-row-wrapper">
                  <DownPaymentInput
                    downPaymentValue={downPaymentValue}
                    onDownPaymentChange={handleDownPaymentChange}
                  />
                  <div className="mortgage-loans-estimate-value-input-wrapper">
                    <EstimateValueInput
                      estimateValue={estimateValue}
                      onEstimateValueChange={handleEstimateValueChange}
                    />
                  </div>
                </div>
                <FormControlLabel
                  control={<Checkbox checked={hasTradeIn} onChange={handleHasTradeIn} />}
                  label={HAS_TRADE_IN}
                />
                {hasTradeIn && (
                  <TradeInValueInput
                    tradeInValue={tradeInValue}
                    onTradeInValueChange={handleTradeInValueChange}
                  />
                )}
                <LoanAmount loanAmountValue={loanAmountValue} />

                <div className="reset-check-rates-button-wrapper">
                  <ResetRatesButton onResetRates={handleResetRates} />
                  <CheckRatesButton
                    loanAmountValue={loanAmountValue}
                    onCheckRates={handleCheckRates}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {checkRates && (
          <div className="rates-comparison-summary-wrapper">
            <ComparisonSummary
              checkRates={checkRates}
              displayFooter={false}
              isFullScreen={isFullScreen}
              isRatesTableFullScreen={isFullScreen}
              setIsFullScreen={setIsFullScreen}
              user={user}
              usState={usState}
              tokenValue={jwt || value}
            />
          </div>
        )}
      </div>
      <Footer currentPath={'/mortgages'} />
    </div>
  );
};
